import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, ShieldCheck, Crown, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const errText = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fb; };
const ROLE_LABEL = { owner: "Owner", admin: "Staff" };

function AdminRow({ a, me, isOwner, onRemove, onRole }) {
  return (
    <div className="admin-row" data-testid={`admin-row-${a.email}`}>
      <div>
        <b>{a.name || a.email}</b>
        <span className={`role-tag ${a.role}`} data-testid={`admin-role-${a.email}`}>{a.role === "owner" && <Crown size={10} />} {ROLE_LABEL[a.role] || a.role}</span>
        {a.id === me?.id && <span className="south-tag" style={{ color: "var(--joy-yellow)", borderColor: "rgba(253,247,30,.4)" }}>You</span>}
        <div className="tbl-email">{a.email}</div>
      </div>
      <div className="admin-row-meta">{a.created_by ? `added by ${a.created_by}` : "original owner"}{a.last_login_at && <div data-testid={`admin-last-login-${a.email}`}>last login {new Date(a.last_login_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>}</div>
      {isOwner && a.id !== me?.id ? (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <select className="admin-search role-select" value={a.role} onChange={(e) => onRole(a, e.target.value)} data-testid={`admin-role-select-${a.email}`}>
            <option value="admin">Staff</option><option value="owner">Owner</option>
          </select>
          <button className="sd-close" onClick={() => onRemove(a)} aria-label={`Remove ${a.email}`} data-testid={`admin-remove-${a.email}`}><Trash2 size={13} /></button>
        </div>
      ) : <span />}
    </div>
  );
}

function AddAdminForm({ onAdded }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const [busy, setBusy] = useState(false);
  const genPw = () => setForm({ ...form, password: Array.from(crypto.getRandomValues(new Uint8Array(10))).map((n) => "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789!#%".charAt(n % 59)).join("") });
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(data.invite_sent ? `${data.email} added as ${ROLE_LABEL[data.role]} — login details emailed ✓` : `${data.email} added (invite email failed — share the password manually)`);
      setForm({ name: "", email: "", password: "", role: "admin" });
      onAdded();
    } catch (err) { toast.error(errText(err, "Could not add admin")); }
    finally { setBusy(false); }
  };
  return (
    <form className="admin-add" onSubmit={add} data-testid="admin-add-form">
      <input className="admin-search" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-add-name" />
      <input className="admin-search" type="email" required placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-add-email" />
      <div className="pw-wrap">
        <input className="admin-search" required minLength={8} placeholder="Temporary password (8+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-add-password" />
        <button type="button" className="pw-gen" onClick={genPw} data-testid="admin-add-generate">Generate</button>
      </div>
      <select className="admin-search role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="admin-add-role">
        <option value="admin">Staff</option><option value="owner">Owner</option>
      </select>
      <button type="submit" className="btn-yellow" disabled={busy} style={{ width: "auto", padding: "9px 16px", fontSize: 13 }} data-testid="admin-add-submit"><UserPlus size={14} style={{ verticalAlign: "-2px", marginRight: 5 }} />{busy ? "Adding…" : "Add"}</button>
    </form>
  );
}

function ChangePasswordForm({ onDone }) {
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/admin/users/me/password", pw); toast.success("Password updated ✓"); onDone(); }
    catch (err) { toast.error(errText(err, "Could not change password")); }
    finally { setBusy(false); }
  };
  return (
    <form className="recip-box pw-form" onSubmit={submit} data-testid="change-password-form">
      <div className="dm-lbl" style={{ marginBottom: 8 }}>Change my password</div>
      <div className="admin-add">
        <input className="admin-search" type="password" required placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" />
        <input className="admin-search" type="password" required minLength={8} placeholder="New password (8+)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
        <button type="submit" className="btn-yellow" disabled={busy} style={{ width: "auto", padding: "9px 16px", fontSize: 13 }} data-testid="pw-submit">Update</button>
      </div>
    </form>
  );
}

function ChangeEmailForm({ currentEmail, onDone }) {
  const [f, setF] = useState({ new_email: "", current_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/admin/users/me/email", f); toast.success(`Login email changed to ${data.email} ✓`); onDone(data.email); }
    catch (err) { toast.error(errText(err, "Could not change email")); }
    finally { setBusy(false); }
  };
  return (
    <form className="recip-box pw-form" onSubmit={submit} data-testid="change-email-form">
      <div className="dm-lbl" style={{ marginBottom: 8 }}>Change my login email <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>(currently {currentEmail})</span></div>
      <div className="admin-add">
        <input className="admin-search" type="email" required placeholder="New email address" value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} data-testid="email-new" />
        <input className="admin-search" type="password" required placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="email-password" />
        <button type="submit" className="btn-yellow" disabled={busy} style={{ width: "auto", padding: "9px 16px", fontSize: 13 }} data-testid="email-submit">Update</button>
      </div>
    </form>
  );
}

export default function AdminAccounts() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [admins, setAdmins] = useState([]);
  const [showPw, setShowPw] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setAdmins(data)).catch((e) => console.warn("Admin list failed", e)), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.email}?`)) return;
    try { await api.delete(`/admin/users/${a.id}`); toast.success(`${a.email} removed`); load(); }
    catch (err) { toast.error(errText(err, "Could not remove")); }
  };
  const setRole = async (a, role) => {
    try { await api.put(`/admin/users/${a.id}/role`, { role }); toast.success(`${a.email} is now ${ROLE_LABEL[role]}`); load(); }
    catch (err) { toast.error(errText(err, "Could not change role")); }
  };

  return (
    <div className="panel" data-testid="admin-accounts-panel">
      <div className="map-head">
        <h3 style={{ margin: 0 }}><ShieldCheck size={16} style={{ verticalAlign: "-2px", marginRight: 8, color: "var(--joy-yellow)" }} />Admin Accounts</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn-secondary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={() => { setShowEmail((v) => !v); setShowPw(false); }} data-testid="change-email-toggle"><Mail size={13} style={{ marginRight: 5, verticalAlign: "-2px" }} />Change my email</button>
          <button className="btn-secondary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={() => { setShowPw((v) => !v); setShowEmail(false); }} data-testid="change-password-toggle"><KeyRound size={13} style={{ marginRight: 5, verticalAlign: "-2px" }} />Change my password</button>
        </div>
      </div>
      <div className="sub-note" data-testid="admin-roles-note">
        <b>Owners</b> can add/remove accounts, change roles, edit digest recipients and send the digest. <b>Staff</b> can view everything and export data.
        {!isOwner && <span> You're signed in as Staff — ask an owner to make changes.</span>}
      </div>
      <div className="recip-box" style={{ marginBottom: 14 }}>
        <div className="recip-list" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          {admins.map((a) => <AdminRow key={a.id} a={a} me={user} isOwner={isOwner} onRemove={remove} onRole={setRole} />)}
        </div>
        {isOwner && <><AddAdminForm onAdded={load} /><div className="edit-hint" style={{ marginTop: 8 }}>We'll email them the login link and temporary password.</div></>}
      </div>
      {showEmail && <ChangeEmailForm currentEmail={user?.email} onDone={() => { setShowEmail(false); load(); window.location.reload(); }} />}
      {showPw && <ChangePasswordForm onDone={() => setShowPw(false)} />}
    </div>
  );
}
